import axios from "axios";
import LocalStorageService from "./localStorageService";
import useAxiosResponse from "hooks/axiosResponse";
import { isHttpSuccess } from "utils/functions";
import { HttpStatus, PANEL_TOKEN_ZERO_TOKEN } from "constant";
import { stringify } from "querystring";
import { pickBy } from "lodash";

const axiosInstance = axios.create({
    headers: {
        "Content-Type": "application/json",
        // So far didn't found usage of text/plain, if its not necessary, feel free to remove it.
        Accept: "application/json,text/plain",
    },
    baseURL: process.env.REACT_APP_DASHBOARD_API,
    paramsSerializer: (params) =>
        stringify(
            pickBy(
                params,
                (value) => value !== null && value !== undefined && value !== ""
            )
        ),
});

// Reference: https://gist.github.com/Godofbrowser/bf118322301af3fc334437c683887c5f
let isRefreshing = false;
let failedQueue: any = [];

const processQueue = (error: any, token = null) => {
    failedQueue.forEach((prom: any) => {
        if (error) {
            prom.reject(error);
        } else {
            prom.resolve(token);
        }
    });

    failedQueue = [];
};

const isMqttQuery = (url?: string) => {
    return url?.match(/(mqtt2tsdb|avg|sum|min|max)_/);
};

const clearStorageExpireSession = () => {
    LocalStorageService.clearAll();
    window.location.replace("/callback?session=expired");
};

window.panelQueryToken = {};

// add request interceptors
axiosInstance.interceptors.request.use(
    (config) => {
        // Set the Authorization header with the AccessToken from local storage
        const { AccessToken: token } = LocalStorageService.getItem("tokens");

        if (token) {
            config.headers.Authorization = `Bearer ${token}`;
        }

        return config;
    },
    (error) => {
        return Promise.reject(error);
    }
);

//add response interceptors
axiosInstance.interceptors.response.use(
    (response) => useAxiosResponse(response),
    async (error) => {
        let originalRequest = error.config;
        const { url } = originalRequest;
        const exceptPath = ["/login", "/account/terminate"].find((u) =>
            url.includes(u)
        );
        const statusCode = error?.response?.status;
        const panelQueryTokenPermissionDenied =
            error.response &&
            [HttpStatus.UNAUTHORIZED, HttpStatus.FORBIDDEN].includes(
                statusCode
            ) &&
            isMqttQuery(url);

        if (
            panelQueryTokenPermissionDenied &&
            url.includes(PANEL_TOKEN_ZERO_TOKEN)
        ) {
            return Promise.reject(error);
        }

        if (
            (error.response &&
                statusCode === HttpStatus.UNAUTHORIZED &&
                !originalRequest._retry &&
                !exceptPath) ||
            (statusCode === HttpStatus.BAD_REQUEST &&
                url.includes("/account/info"))
        ) {
            if (
                [
                    "Invalid Refresh Token",
                    "Refresh Token has been revoked",
                ].includes(error.response.data.description)
            ) {
                clearStorageExpireSession();
            }

            if (isRefreshing) {
                // All requests during refresh are queued and retried with new token
                return new Promise((resolve, reject) => {
                    failedQueue.push({
                        resolve: (token: string) => {
                            originalRequest.headers["Authorization"] =
                                "Bearer " + token;
                            resolve(axiosInstance(originalRequest));
                        },
                        reject: (err: any) => {
                            reject(err);
                        },
                    });
                });
            }

            originalRequest._retry = true;
            isRefreshing = true;

            // Only the first request triggers the refresh
            const { RefreshToken } =
                LocalStorageService.getItem("tokens") || {};

            if (!RefreshToken) {
                clearStorageExpireSession();
            }

            try {
                const response = await axios.post(
                    `${process.env.REACT_APP_DASHBOARD_API}/account/token`,
                    {},
                    {
                        headers: {
                            Authorization: `Bearer ${RefreshToken}`,
                        },
                    }
                );

                if (isHttpSuccess(response.status)) {
                    const { AccessToken: NewAccessToken } = response.data.data;

                    LocalStorageService.setItem("tokens", {
                        ...LocalStorageService.getItem("tokens"),
                        AccessToken: NewAccessToken,
                    });

                    axiosInstance.defaults.headers.common[
                        "Authorization"
                    ] = `Bearer ${NewAccessToken}`;

                    processQueue(null, NewAccessToken);

                    // Retry the original request with the new token
                    originalRequest.headers[
                        "Authorization"
                    ] = `Bearer ${NewAccessToken}`;
                    return axiosInstance(originalRequest);
                } else {
                    processQueue(error, null);
                    return Promise.reject(error);
                }
            } catch (err: any) {
                processQueue(err, null);
                clearStorageExpireSession();
                return Promise.reject(err);
            } finally {
                isRefreshing = false;
            }
        }
        return Promise.reject(error);
    }
);

export default axiosInstance;
